import { IconButton } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import * as React from 'react';

import styles from './messageAudio.module.scss';

type MessageAudioProps = {
  className?: string;
  soundfile: {
    src: (() => Promise<string>) | string;
    type: string;
  };
  title: string;
  onDownload: () => void;
  date: number;
  conversation: any;
};

type StateT = {
  playing: boolean;
  actuallyPlaying: boolean;
  progress: number;
  duration: number;
  changingDuration: number;
  audioSrc: string;
};

type PhoneRecordingEvent =
  | 'play'
  | 'pause'
  | 'navigated_away'
  | 'ended'
  | 'downloaded';

type EventDataT = {
  csid: number;
  createdAt: number;
  direction: string;
  channel: string;
  recordingDuration: number;
  progress: number;
  currentTimestamp: number;
};

function generateTimeStamp(duration: number) {
  const stampLength = duration >= 3600 ? 8 : 5;
  if (Number.isNaN(duration)) return '00:00';
  return new Date(duration * 1000)
    .toISOString()
    .substr(19 - stampLength, stampLength);
}

export class MessageAudio extends React.Component<MessageAudioProps, StateT> {
  audioRef: React.RefObject<HTMLAudioElement>;
  playerRef: React.RefObject<HTMLDivElement>;

  startX?: number;
  playerWidth?: number;
  startProgress?: number;

  constructor(props: MessageAudioProps) {
    super(props);

    this.state = {
      playing: false,
      actuallyPlaying: false,
      progress: 0,
      duration: 0,
      changingDuration: 0,
      audioSrc: '',
    };

    this.handleSegmentEvent = this.handleSegmentEvent.bind(this);

    this.audioRef = React.createRef();
    this.playerRef = React.createRef();
    this.handleTogglePlayClick = this.handleTogglePlayClick.bind(this);
    this.updateProgress = this.updateProgress.bind(this);

    this.handleHandleGrab = this.handleHandleGrab.bind(this);
    this.handleHandleMove = this.handleHandleMove.bind(this);
    this.handleHandleDrop = this.handleHandleDrop.bind(this);
    this.handlePlayerClick = this.handlePlayerClick.bind(this);
    this.handleRecordingDownload = this.handleRecordingDownload.bind(this);

    this.setAudio = this.setAudio.bind(this);
  }

  componentDidMount() {
    if (this.audioRef.current) {
      this.audioRef.current.addEventListener('loadeddata', () => {
        if (this.audioRef.current) {
          const { duration } = this.audioRef.current;
          this.setState({
            duration,
            changingDuration: duration,
          });
        }
      });
    }

    if (this.audioRef.current) {
      const audioRef = this.audioRef.current;
      this.audioRef.current.addEventListener('ended', () => {
        this.setState({
          playing: false,
        });
        this.setProgress(0);
        this.handleSegmentEvent(audioRef, 'ended');
      });
    }

    this.loadAudioSrc();
  }

  componentWillUnmount() {
    if (this.state.playing && this.audioRef.current) {
      this.handleSegmentEvent(this.audioRef.current, 'navigated_away');
    }
  }

  handleSegmentEvent(
    element: HTMLAudioElement,
    event: PhoneRecordingEvent,
  ): void {
    const { currentTime, duration } = element;
    const { conversation, date } = this.props;

    const eventData: EventDataT = {
      csid: conversation.id,
      createdAt: Math.floor(new Date(date).getTime()),
      direction: conversation.direction,
      channel: conversation.initial_channel,
      recordingDuration: duration,
      progress: event === 'ended' ? duration : currentTime,
      currentTimestamp: new Date().getTime(),
    };

    switch (event) {
      case 'play':
        ampli.phoneRecordingPlayClicked(eventData);
        break;
      case 'pause':
        ampli.phoneRecordingPauseClicked(eventData);
        break;
      case 'navigated_away':
        ampli.phoneRecordingPlaybackAbandoned(eventData);
        break;
      case 'ended':
        ampli.phoneRecordingPlaybackEnded(eventData);
        break;
      case 'downloaded':
        ampli.phoneRecordingDownloaded(eventData);
        break;
    }
  }

  updateProgress() {
    if (this.audioRef.current) {
      const { currentTime, duration } = this.audioRef.current;
      const { playing } = this.state;

      this.setState({
        progress: currentTime / duration,
        changingDuration: duration,
      });

      if (playing) {
        window.requestAnimationFrame(this.updateProgress);
      }
    }
  }

  handleTogglePlayClick() {
    const { playing, actuallyPlaying } = this.state;

    if (this.audioRef.current) {
      if (playing && actuallyPlaying) {
        this.handleSegmentEvent(this.audioRef.current, 'pause');

        this.audioRef.current.pause();
        this.setState({
          playing: false,
          actuallyPlaying: false,
        });
      } else if (!playing) {
        this.handleSegmentEvent(this.audioRef.current, 'play');

        this.setState({
          playing: true,
        });
        this.audioRef.current.play().then(() => {
          this.setState({
            actuallyPlaying: true,
          });
        });
        window.requestAnimationFrame(this.updateProgress);
      }
    }
  }

  handleRecordingDownload() {
    this.props.onDownload();
    if (this.audioRef.current) {
      this.handleSegmentEvent(this.audioRef.current, 'downloaded');
    }
  }

  handleHandleMove(event: MouseEvent) {
    event.preventDefault();
    if (this.startProgress && this.startX && this.playerWidth) {
      let p =
        this.startProgress + (event.clientX - this.startX) / this.playerWidth;
      if (p > 1) p = 1;
      if (p < 0) p = 0;
      this.setProgress(p);
    }
  }

  handleHandleDrop() {
    window.removeEventListener('mousemove', this.handleHandleMove);
    window.removeEventListener('mouseup', this.handleHandleDrop);
  }

  handleHandleGrab(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (this.playerRef.current) {
      const { progress } = this.state;
      this.startX = event.clientX;
      this.startProgress = progress;
      this.playerWidth = this.playerRef.current.getBoundingClientRect().width;

      window.addEventListener('mousemove', this.handleHandleMove);
      window.addEventListener('mouseup', this.handleHandleDrop);
    }
  }

  handlePlayerClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (this.playerRef.current) {
      this.startX = event.clientX;
      const bounds = this.playerRef.current.getBoundingClientRect();
      const p = (event.clientX - bounds.left) / bounds.width;
      this.setProgress(p);

      this.startProgress = p;
      this.playerWidth = bounds.width;

      window.addEventListener('mousemove', this.handleHandleMove);
      window.addEventListener('mouseup', this.handleHandleDrop);
    }
  }

  setProgress(progress: number) {
    if (this.audioRef.current) {
      const { duration } = this.state;
      this.audioRef.current.currentTime = progress * duration;
    }
    this.updateProgress();
  }

  loadAudioSrc() {
    const {
      soundfile: { src },
    } = this.props;

    if (typeof src === 'string') {
      this.setAudio(src);
    } else {
      src()
        .then((result: string) => {
          this.setAudio(result);
        })
        .catch(() => {
          /* do nothing */
        });
    }
  }

  setAudio(src: string) {
    this.setState({
      audioSrc: src,
      duration: 0,
      progress: 0,
      playing: false,
      changingDuration: 0,
    });
  }

  render() {
    const { className, soundfile, title, ...otherProps } = this.props;

    const { playing, progress, duration, changingDuration, audioSrc } =
      this.state;

    const rootClass = classnames(
      {
        [styles.messageAudio]: true,
      },
      className,
    );

    return (
      <div {...otherProps} className={rootClass}>
        <IconButton
          variant="secondary"
          icon={playing ? 'pause' : 'play'}
          className={styles.control}
          onClick={this.handleTogglePlayClick}
        />
        <IconButton
          variant="secondary"
          icon="cloud-download"
          className={styles.control}
          onClick={this.handleRecordingDownload}
        />

        <div className={styles.playerWrap}>
          <p className={styles.title}>{title}</p>
          <time className={styles.timestamp}>
            {generateTimeStamp(progress * duration)}
          </time>

          <div
            className={styles.player}
            ref={this.playerRef}
            onMouseDown={this.handlePlayerClick}
          >
            <div
              className={styles.handle}
              style={{
                left: `${progress * 100}%`,
              }}
              onMouseDown={this.handleHandleGrab}
            />
            <div
              className={styles.progress}
              style={{
                width: `${progress * 100}%`,
              }}
            />
          </div>

          <time className={styles.timestamp}>
            {generateTimeStamp(changingDuration)}
          </time>
        </div>

        <audio ref={this.audioRef}>
          {audioSrc && <source src={audioSrc} type={soundfile.type} />}
        </audio>
      </div>
    );
  }
}
